// LandlordDashboard — the /admin landing for tenants with industry=landlord.
// Replaces the PM Dispatch Board (which is dispatch-heavy and wrong for a
// solo landlord). Focus: What do I owe attention to TODAY?
//   • Rent due this month — per-rental paid/unpaid card with Pay Log
//   • Open repair requests — count + list, tap to inbox
//   • Active applications — count + list, tap to inbox
//   • Vacant rentals — nudge to mint an apply link
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import {
  House, Wrench, User, Sparkle, CheckCircle, ArrowRight, Warning,
  CreditCard, Buildings, PaperPlaneTilt, Copy as CopyIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { LegalHealthChip } from "@/pages/landlord/ComplianceLayer";

const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const monthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = () => new Date().toLocaleString("en-US", { month: "long", year: "numeric" });

export default function LandlordDashboard() {
  const [rentals, setRentals] = useState([]);
  const [repairs, setRepairs] = useState({ items: [], counts: {} });
  const [appsByRental, setAppsByRental] = useState({});   // { rental_id: [apps] }
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [rRentals, rRepairs] = await Promise.all([
        api.get("/landlord-auto/rentals"),
        api.get("/landlord-auto/repairs"),
      ]);
      const rlist = rRentals.data.rentals || [];
      setRentals(rlist);
      setRepairs({ items: rRepairs.data.repairs || [], counts: rRepairs.data.counts || {} });
      // Applications are per-rental — fire in parallel, ignore errors on empty rentals
      const perRental = await Promise.all(rlist.map(async r => {
        try { const a = await api.get(`/landlord-auto/rentals/${r.id}/applications`); return [r.id, a.data.applications || []]; }
        catch { return [r.id, []]; }
      }));
      setAppsByRental(Object.fromEntries(perRental));
    } catch (e) { toast.error("Could not load dashboard"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Derived numbers
  const period = monthKey();
  const stats = useMemo(() => {
    const rentDue = rentals.filter(r => {
      // "Paid this month" = last_paid_at falls inside current month OR method flag on last payment matches period
      if (!r.rent_monthly_cents) return false;
      if (!r.last_paid_at) return true;
      return !r.last_paid_at.startsWith(period.split("-")[0] + "-" + period.split("-")[1]);
    });
    const collected = rentals.reduce((s, r) => {
      if (r.last_paid_at && r.last_paid_at.startsWith(period)) s += r.last_paid_cents || 0;
      return s;
    }, 0);
    const totalExpected = rentals.reduce((s, r) => s + (r.rent_monthly_cents || 0), 0);
    const openRepairs = (repairs.counts?.new || 0) + (repairs.counts?.triaged || 0) + (repairs.counts?.dispatched || 0) + (repairs.counts?.in_progress || 0);
    const newApps = Object.values(appsByRental).flat().filter(a => !a.decision).length;
    const vacantRentals = rentals.filter(r => !r.renter_name);
    return { rentDue, collected, totalExpected, openRepairs, newApps, vacantRentals };
  }, [rentals, repairs, appsByRental, period]);

  const copyApplyUrl = async (rid) => {
    try {
      // Ensure the apply token exists, then copy
      const r = await api.post(`/landlord-auto/rentals/${rid}/application-link`);
      await navigator.clipboard.writeText(r.data.apply_url);
      toast.success("Application link copied");
    } catch { toast.error("Could not copy"); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6" data-testid="landlord-dashboard">
        {/* Photo-backed hero */}
        <div className="relative overflow-hidden rounded-2xl mb-6 shadow-lg border border-slate-800 bg-slate-950" data-testid="landlord-hero">
          <img
            src="https://images.unsplash.com/photo-1512621480870-77463b1b90c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHx1cmJhbiUyMGJyb3duc3RvbmUlMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMHJlbnRhbHN8ZW58MHx8fHwxNzg3NTY5NjU1fDA&ixlib=rb-4.1.0&q=85"
            alt="Rental Portfolio Background"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-45 transform scale-105 transition-transform duration-700 hover:scale-100"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/80 to-slate-950/40 backdrop-blur-[1px]" />
          <div className="relative z-10 p-5 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/30 backdrop-blur-md mb-2.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-300">Live Portfolio Snapshot</span>
              </div>
              <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-none">
                Today · {monthLabel()}
              </h1>
              <div className="text-xs sm:text-sm font-medium text-slate-300/90 mt-2 flex items-center gap-2 flex-wrap">
                <span className="font-bold text-white">{rentals.length} rental{rentals.length === 1 ? "" : "s"}</span>
                <span className="text-slate-500">•</span>
                <span>{dollars(stats.collected)} collected</span>
                <span className="text-slate-500">•</span>
                <span className={stats.openRepairs > 0 ? "text-amber-300 font-bold" : "text-slate-300"}>{stats.openRepairs} open repair{stats.openRepairs === 1 ? "" : "s"}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <LegalHealthChip />
              <Link to="/admin/landlord/rentals" data-testid="dash-manage-rentals-btn"
                className="h-11 px-5 bg-white hover:bg-slate-100 text-slate-900 font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 active:translate-y-0">
                <Buildings weight="fill" size={16} className="text-slate-700" /> Manage Rentals
              </Link>
            </div>
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="dash-kpis">
          <KPI to="/admin/landlord/rentals" label="Rent collected" value={dollars(stats.collected)}
            sub={`of ${dollars(stats.totalExpected)} · ${monthLabel()}`}
            accent="emerald" icon={CreditCard} testid="dash-kpi-collected"/>
          <KPI to="/admin/landlord/rentals" label="Rent due" value={String(stats.rentDue.length)}
            sub={stats.rentDue.length === 1 ? "unit outstanding" : "units outstanding"}
            accent={stats.rentDue.length > 0 ? "amber" : "slate"} icon={Warning} testid="dash-kpi-due"/>
          <KPI to="/admin/landlord/repairs" label="Open repairs" value={String(stats.openRepairs)}
            sub="new + dispatched"
            accent={stats.openRepairs > 0 ? "sky" : "slate"} icon={Wrench} testid="dash-kpi-repairs"/>
          <KPI to={stats.newApps > 0 ? "/admin/landlord/rentals" : "/admin/landlord/rentals"} label="New applications" value={String(stats.newApps)}
            sub={stats.vacantRentals.length > 0 ? `${stats.vacantRentals.length} vacant unit${stats.vacantRentals.length === 1 ? "" : "s"}` : "no vacancies"}
            accent={stats.newApps > 0 ? "fuchsia" : "slate"} icon={User} testid="dash-kpi-apps"/>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Rent Due panel */}
          <section className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm" data-testid="dash-rent-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center"><CreditCard weight="fill" size={16}/></div>
                <div>
                  <div className="font-display font-black text-lg leading-none">Rent · {monthLabel()}</div>
                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mt-0.5">{rentals.length ? `${rentals.length - stats.rentDue.length}/${rentals.length} paid` : "no rentals yet"}</div>
                </div>
              </div>
            </div>
            {loading ? (
              <SkeletonRows/>
            ) : rentals.length === 0 ? (
              <EmptyState msg="Add your first rental to start collecting" cta="Add Rental" to="/admin/landlord/rentals" testid="dash-rent-empty"/>
            ) : (
              <div className="space-y-2">
                {rentals.map(r => {
                  const paidThisMonth = r.last_paid_at && r.last_paid_at.startsWith(period);
                  return (
                    <div key={r.id} data-testid={`dash-rent-row-${r.id}`}
                      className="p-3 border border-slate-200 rounded-xl flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate">{r.unit_label}</div>
                        <div className="text-[11px] text-slate-500 truncate">{r.renter_name || "(no renter)"}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-mono font-bold text-sm">{dollars(r.rent_monthly_cents)}</div>
                        {paidThisMonth ? (
                          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600 inline-flex items-center gap-0.5"><CheckCircle weight="fill" size={9}/> Paid</div>
                        ) : (
                          <div className="text-[10px] font-black uppercase tracking-widest text-amber-600">Due</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Open Repairs panel */}
          <section className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm" data-testid="dash-repairs-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-sky-600 text-white flex items-center justify-center"><Wrench weight="fill" size={16}/></div>
                <div>
                  <div className="font-display font-black text-lg leading-none">Repairs</div>
                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mt-0.5">{stats.openRepairs} open · {repairs.counts?.resolved || 0} resolved</div>
                </div>
              </div>
              <Link to="/admin/landlord/repairs" data-testid="dash-repairs-open-inbox"
                className="text-[10px] font-black uppercase tracking-widest text-sky-600 hover:text-sky-800 inline-flex items-center gap-1">
                Open Inbox <ArrowRight size={11}/>
              </Link>
            </div>
            {loading ? (
              <SkeletonRows/>
            ) : (repairs.items || []).filter(r => r.status !== "resolved" && r.status !== "cancelled").length === 0 ? (
              <EmptyState msg="No open repairs — you're clear" testid="dash-repairs-empty"/>
            ) : (
              <div className="space-y-2">
                {(repairs.items || [])
                  .filter(r => r.status !== "resolved" && r.status !== "cancelled")
                  .slice(0, 5)
                  .map(r => (
                    <Link key={r.id} to="/admin/landlord/repairs" data-testid={`dash-repair-row-${r.id}`}
                      className="p-3 border border-slate-200 rounded-xl flex items-center gap-3 hover:border-sky-400 hover:bg-sky-50/40 transition">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
                            r.status === "dispatched" ? "bg-emerald-100 text-emerald-700" :
                            r.status === "triaged" ? "bg-sky-100 text-sky-700" :
                            r.status === "in_progress" ? "bg-amber-100 text-amber-700" :
                            "bg-fuchsia-100 text-fuchsia-700"
                          }`}>{r.status.replace("_", " ")}</span>
                          <span className="font-bold text-sm truncate">{r.title}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">{r.rental_label || "(unlabeled rental)"} · {r.submitter_name || "renter"}</div>
                      </div>
                      <ArrowRight size={14} className="text-slate-400 flex-shrink-0"/>
                    </Link>
                  ))}
              </div>
            )}
          </section>

          {/* Applications panel */}
          <section className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm lg:col-span-2" data-testid="dash-apps-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-fuchsia-600 text-white flex items-center justify-center"><User weight="fill" size={16}/></div>
                <div>
                  <div className="font-display font-black text-lg leading-none">Applications</div>
                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mt-0.5">
                    {stats.newApps} pending decision · {stats.vacantRentals.length} vacant unit{stats.vacantRentals.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
            </div>
            {loading ? (
              <SkeletonRows/>
            ) : Object.values(appsByRental).flat().length === 0 && stats.vacantRentals.length === 0 ? (
              <EmptyState msg="No open applications and no vacancies — quiet day" testid="dash-apps-empty"/>
            ) : (
              <div className="space-y-3">
                {/* Vacant rentals — nudge to mint apply link */}
                {stats.vacantRentals.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="dash-vacant-block">
                    {stats.vacantRentals.map(r => (
                      <div key={r.id} data-testid={`dash-vacant-${r.id}`}
                        className="p-3 border-2 border-dashed border-fuchsia-300 bg-fuchsia-50/50 rounded-xl">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm">{r.unit_label}</div>
                            <div className="text-[11px] text-slate-500 truncate">{r.address}</div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 mt-1">Vacant · accept applications</div>
                          </div>
                          <button onClick={() => copyApplyUrl(r.id)} data-testid={`dash-vacant-copy-${r.id}`}
                            className="h-8 px-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[9px] rounded-lg inline-flex items-center gap-1 flex-shrink-0">
                            <CopyIcon weight="fill" size={10}/> Copy Apply Link
                          </button>
                        </div>
                        <Link to={`/admin/landlord/rentals/${r.id}/applications`}
                          className="mt-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-fuchsia-700 hover:text-fuchsia-900"
                          data-testid={`dash-vacant-view-apps-${r.id}`}>
                          View applications <ArrowRight size={10}/>
                        </Link>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pending applications */}
                {Object.entries(appsByRental).map(([rid, apps]) => {
                  const pending = (apps || []).filter(a => !a.decision);
                  if (pending.length === 0) return null;
                  const rental = rentals.find(r => r.id === rid);
                  return (
                    <div key={rid} data-testid={`dash-apps-group-${rid}`}>
                      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">
                        {rental?.unit_label || "Unit"} · {pending.length} pending
                      </div>
                      <div className="space-y-1.5">
                        {pending.slice(0, 3).map(a => {
                          const s = a.screening;
                          const ratio = rental?.rent_monthly_cents
                            ? ((a.monthly_income_cents || 0) / rental.rent_monthly_cents).toFixed(2)
                            : null;
                          return (
                            <Link key={a.id} to={`/admin/landlord/rentals/${rid}/applications`} data-testid={`dash-app-row-${a.id}`}
                              className="p-3 border border-slate-200 rounded-xl flex items-center gap-2 hover:border-fuchsia-400 hover:bg-fuchsia-50/40 transition">
                              {s?.triage && (
                                <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded flex-shrink-0 ${
                                  s.triage === "GREEN" ? "bg-emerald-500 text-white" :
                                  s.triage === "YELLOW" ? "bg-amber-500 text-white" :
                                  "bg-red-500 text-white"
                                }`}>{s.triage}</span>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm truncate">{a.applicant_name}</div>
                                <div className="text-[11px] text-slate-500 truncate">
                                  {ratio && <>Income {ratio}x · </>}{a.credit_score_range}
                                </div>
                              </div>
                              <ArrowRight size={14} className="text-slate-400 flex-shrink-0"/>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <div className="mt-6 text-center text-[10px] text-slate-400">
          Landlord Autopilot · Field Radar
        </div>
      </div>
    </AdminShell>
  );
}

// ─── Bits ─────────────────────────────────────────────────────
const ACCENT_MAP = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800 hover:border-emerald-500",
  amber:   "bg-amber-50 border-amber-200 text-amber-800 hover:border-amber-500",
  sky:     "bg-sky-50 border-sky-200 text-sky-800 hover:border-sky-500",
  fuchsia: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800 hover:border-fuchsia-500",
  slate:   "bg-white border-slate-200 text-slate-700 hover:border-slate-400",
};
function KPI({ to, label, value, sub, accent = "slate", icon: Icon, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className={`p-4 rounded-2xl border-2 transition ${ACCENT_MAP[accent] || ACCENT_MAP.slate}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest opacity-80">
        {Icon && <Icon weight="fill" size={12}/>} {label}
      </div>
      <div className="mt-1 font-display font-black text-3xl leading-tight">{value}</div>
      <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>
    </Link>
  );
}
function SkeletonRows() {
  return (
    <div className="space-y-2 animate-pulse">
      {[1,2,3].map(i => <div key={i} className="h-14 bg-slate-100 rounded-xl"/>)}
    </div>
  );
}
function EmptyState({ msg, cta, to, testid }) {
  return (
    <div className="p-6 border-2 border-dashed border-slate-200 rounded-xl text-center" data-testid={testid}>
      <div className="text-sm text-slate-500">{msg}</div>
      {cta && to && (
        <Link to={to} className="mt-2 inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-sky-600 hover:text-sky-800">
          {cta} <ArrowRight size={12}/>
        </Link>
      )}
    </div>
  );
}
